import { prepareForSlot } from '@mui/base'
import { clsx } from 'clsx'
import { type FC } from 'react'

import { type CrossSellProduct } from '@redteclab/api/clients/retail-media-xsell-service'

import { Link, Skeleton } from 'base-ui'

import { experimentsCompeteraTrackCompeteraEvent } from '../../../experiments/competera-experiment/experimentsCompeteraTrackCompeteraEvent'
import { useExperimentsContext } from '../../../experiments/components/ExperimentsContext'
import { EXPERIMENTS_TRACK_EVENT_CSW_COMPETERA } from '../../../experiments/eventTracking'
import {
  CSW_INTERACTED_KEY,
  useSetSessionStorageValuesForOptimizelyTracking,
} from '../../../experiments/hooks'
import { useGlobalConfigContext } from '../../../global-config'
import { NextLink } from '../../../next-link'
import { urlGetRelativeUrl } from '../../../url-handling'
import { crossSellAccessorGetLocalizedProductDeepLink } from '../../model/crossSellAccessorGetLocalizedProductDeepLink'
import { crossSellAccessorGetLocalizedProductName } from '../../model/crossSellAccessorGetLocalizedProductName'
import { crossSellAccessorGetSponsoredProductLink } from '../../model/crossSellAccessorGetSponsoredProductLink'
import { useCrossSellMetadataContext } from '../CrossSellMetatadaProvider'

const LinkSlot = prepareForSlot(NextLink)

type CrossSellProductNameProps = {
  className?: string
  product?: CrossSellProduct
}

export const CrossSellProductName: FC<CrossSellProductNameProps> = ({
  className,
  product,
}) => {
  const globalConfig = useGlobalConfigContext()
  const { language, tenant } = globalConfig
  const trackClick =
    useSetSessionStorageValuesForOptimizelyTracking(CSW_INTERACTED_KEY)
  const metadata = useCrossSellMetadataContext()
  const { experiments } = useExperimentsContext()

  if (!product) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="w-full" />
        <Skeleton className="w-full" />
        <Skeleton className="w-3/4" />
      </div>
    )
  }

  const sponsoredProductLink = crossSellAccessorGetSponsoredProductLink({
    metadata,
    product,
  })

  const productName = crossSellAccessorGetLocalizedProductName({
    language,
    product,
    tenant,
  })

  return (
    <Link
      className={clsx('cross-sell-product-card-name', className)}
      data-analytics="item-title"
      data-qa-id={`form-product-slider-${product.id}-link-button`}
      href={
        sponsoredProductLink ??
        urlGetRelativeUrl(
          crossSellAccessorGetLocalizedProductDeepLink({
            language,
            product,
            tenant,
          }),
        )
      }
      onClick={(): void => {
        trackClick()
        experimentsCompeteraTrackCompeteraEvent(
          experiments,
          globalConfig,
          EXPERIMENTS_TRACK_EVENT_CSW_COMPETERA,
        )
      }}
      rel={sponsoredProductLink ? 'nofollow noopener' : undefined}
      slots={{ root: LinkSlot }}
      title={productName}
    >
      {productName}
    </Link>
  )
}
